import { apiRequest, ApiClientError } from "./apiClient";
export type ActionReceipt = {
  receipt_id: string;
  resource_type: string;
  resource_id: string;
  result_version: number;
  status: "committed";
};
type Decision = {
  key: string;
  signature: string;
  headers: Record<string, string>;
};
export class UnknownMutationResultError extends Error {
  readonly cause: unknown;
  constructor(
    message = "Result not yet confirmed. Your draft is preserved. Retry the same decision to check its receipt and current record.",
    cause?: unknown,
  ) {
    super(message);
    this.cause = cause;
    this.name = "UnknownMutationResultError";
  }
}
const pending = new Map<string, Decision>();
export function clearPendingDecisions() {
  pending.clear();
}

/** Retain the original decision identity until a committed result can be read. */
export async function guardedMutation<T>(
  path: `/${string}`,
  method: string,
  body: object | undefined,
  expected: Record<string, number>,
  readCanonical: (receipt?: ActionReceipt) => Promise<T>,
): Promise<T> {
  const signature = JSON.stringify([method, path, body, expected]);
  const scope = `${path}:${Object.keys(expected).sort().join(",")}`;
  const prior = pending.get(scope);
  if (prior && prior.signature !== signature)
    throw new UnknownMutationResultError(
      "Result not yet confirmed. Keep the original draft and retry that decision before changing it.",
    );
  const decision = prior ?? {
    key: crypto.randomUUID(),
    signature,
    headers: {
      "Idempotency-Key": "",
      "X-Expected-Versions": JSON.stringify(expected),
    },
  };
  decision.headers["Idempotency-Key"] = decision.key;
  pending.set(scope, decision);
  let commandRejected = false;
  try {
    if (prior) {
      const receipt = await lookupReceipt(path, decision.key);
      if (receipt) {
        const resource = await readCanonical(receipt);
        pending.delete(scope);
        return resource;
      }
    }
    let replayed = false;
    commandRejected = true;
    const result = await apiRequest<T | ActionReceipt>(
      path,
      {
        method,
        headers: decision.headers,
        signal: AbortSignal.timeout(20_000),
        ...(body ? { body: JSON.stringify(body) } : {}),
      },
      (headers) => {
        replayed = headers.get("Idempotency-Replayed") === "true";
      },
    );
    commandRejected = false;
    const resource = replayed
      ? await readCanonical(result as ActionReceipt)
      : (result as T);
    pending.delete(scope);
    return resource;
  } catch (error) {
    if (
      commandRejected &&
      error instanceof ApiClientError &&
      error.problem.status >= 400 &&
      error.problem.status < 500
    ) {
      pending.delete(scope);
      throw error;
    }
    throw new UnknownMutationResultError(undefined, error);
  }
}
async function lookupReceipt(
  path: string,
  key: string,
): Promise<ActionReceipt | null> {
  const prefix = path.startsWith("/exchange/market/") ? "/exchange/market" : "";
  try {
    return await apiRequest<ActionReceipt>(`${prefix}/action-receipts/${key}`);
  } catch (error) {
    if (error instanceof ApiClientError && error.problem.status === 404)
      return null;
    throw error;
  }
}
